import { scaleToGrams } from '@gramas/shared'
import {
  Button,
  Chip,
  EmptyState,
  List,
  ListRow,
  MicIcon,
  NumberField,
  Pill,
  Sheet,
} from '@gramas/ui'
import { useEffect, useRef, useState } from 'react'
import { foodDisplayName, foodPer100g } from '../../db/repo.js'
import { GlitimeMark } from '../../glitime/Mark.js'
import { sassLine } from '../../sass/engine.js'
import { useSassOnce } from '../../sass/useSassOnce.js'
import { useVoiceLog, type VoiceLog } from '../../voice/useVoiceLog.js'

export interface VoiceLogSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  date: string
  /** Fired only when something was actually written down, never on cancel. */
  onLogged?: () => void
}

/**
 * Say what you ate, correct what it got wrong.
 *
 * Two rules the first version broke. Holding a button is the gesture: a tap
 * that starts an open microphone leaves you wondering whether it is still
 * listening, while a held button ends exactly when you stop pressing. And the
 * transcript is never shown — watching words appear and rewrite themselves
 * turns speaking into proofreading. What comes back is a list of foods and
 * amounts, every one of them editable, because that is the part worth checking.
 */
export function VoiceLogSheet({ open, onOpenChange, date, onLogged }: VoiceLogSheetProps) {
  const voice = useVoiceLog(date, () => {
    onOpenChange(false)
    onLogged?.()
  })
  const [typed, setTyped] = useState('')
  const { reset } = voice

  // Both edges. Opening starts clean; closing has to put the microphone down —
  // the sheet unmounts its own hold button, so a dismissal mid-sentence took the
  // release with it and left the recorder running to its cap, indicator lit,
  // before spending a model call on a conversation nobody was having.
  useEffect(() => {
    reset()
    if (open) setTyped('')
  }, [open, reset])

  const { phase, mic } = voice

  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
      title="Įrašyti balsu"
      footer={phase.name === 'review' ? <ReviewFooter voice={voice} /> : undefined}
    >
      <div className="flex flex-col gap-4 px-4 py-4">
        {phase.name === 'review' ? (
          <ReviewList voice={voice} />
        ) : (
          <>
            <div className="flex gap-1.5 self-center rounded-full border border-line p-0.5">
              {(['lt', 'en'] as const).map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => voice.setLang(option)}
                  className={`rounded-full px-3 py-1 text-sm ${
                    voice.lang === option ? 'bg-raised text-ink' : 'text-mute'
                  }`}
                >
                  {option === 'lt' ? 'Lietuvių' : 'English'}
                </button>
              ))}
            </div>

            {mic.supported ? (
              <HoldToTalk
                phase={phase.name}
                onStart={() => mic.start()}
                onStop={() => mic.stop()}
              />
            ) : (
              <div className="flex flex-col gap-3">
                <Chip tone="warn" className="self-start">
                  Šis įrenginys neleidžia įrašyti — parašykite
                </Chip>
                <textarea
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  rows={3}
                  placeholder="pvz. 150 gramų varškės ir 20 gramų medaus"
                  className="w-full resize-none rounded-[var(--radius-control)] border border-line bg-bg px-3 py-2 text-base text-ink placeholder:text-mute"
                />
                <Button
                  variant="primary"
                  block
                  disabled={typed.trim().length === 0 || phase.name === 'working'}
                  onClick={() => void voice.parse(typed.trim())}
                >
                  Atpažinti
                </Button>
              </div>
            )}

            {phase.name === 'failed' && <p className="text-sm text-danger">{phase.message}</p>}
            {mic.error != null && phase.name !== 'failed' && (
              <p className="text-sm text-danger">{mic.error}</p>
            )}
            {phase.name === 'idle' && mic.supported && (
              <EmptyState
                title="Laikykite mygtuką ir sakykite"
                hint="„Du kiaušiniai ir riekė duonos“ — paleidus mygtuką bus atpažinta."
              />
            )}
          </>
        )}
      </div>
    </Sheet>
  )
}

/**
 * The review half on its own, for the Scan screen.
 *
 * There the microphone is the camera's own shutter, so by the time anything
 * needs a sheet the speaking is over and only the correcting is left.
 */
export function VoiceReviewSheet({
  voice,
  onOpenChange,
}: {
  voice: VoiceLog
  onOpenChange: (open: boolean) => void
}) {
  const failed = voice.phase.name === 'failed'
  return (
    <Sheet
      open={voice.phase.name === 'review' || failed}
      onOpenChange={(open) => {
        if (!open) voice.reset()
        onOpenChange(open)
      }}
      title={failed ? 'Nepavyko' : 'Patikslinkite'}
      footer={voice.phase.name === 'review' ? <ReviewFooter voice={voice} /> : undefined}
    >
      <div className="flex flex-col gap-4 px-4 py-4">
        {voice.phase.name === 'review' ? (
          <ReviewList voice={voice} />
        ) : (
          <EmptyState
            title={failed && voice.phase.name === 'failed' ? voice.phase.message : ''}
            hint="Laikykite mygtuką ir pasakykite dar kartą."
          />
        )}
      </div>
    </Sheet>
  )
}

function ReviewFooter({ voice }: { voice: VoiceLog }) {
  // Only rows with an amount. A cleared field used to commit as 0 g, and a
  // zero-quantity entry cannot be edited afterwards without dividing by it —
  // which wrote NaN into the diary and then into the whole day's totals.
  const count =
    voice.phase.name === 'review' ? voice.phase.items.filter((i) => i.grams > 0).length : 0
  const incomplete =
    voice.phase.name === 'review' ? voice.phase.items.some((i) => !(i.grams > 0)) : false
  return (
    <div className="flex gap-2">
      <Button block onClick={voice.reset} disabled={voice.saving}>
        Iš naujo
      </Button>
      <Button
        variant="primary"
        block
        onClick={() => void voice.commitAll()}
        disabled={voice.saving || count === 0 || incomplete}
      >
        Įrašyti {Math.round(voice.total)} kcal
      </Button>
    </div>
  )
}

function ReviewList({ voice }: { voice: VoiceLog }) {
  const [editing, setEditing] = useState<string | null>(null)
  if (voice.phase.name !== 'review') return null
  const items = voice.phase.items

  return (
    <>
      <p className="text-sm text-mute">Palieskite eilutę, kad pakeistumėte produktą ar kiekį.</p>
      <List className="overflow-hidden rounded-[var(--radius-card)] border border-line">
        {items.map((item, i) => {
          const preview = item.food ? scaleToGrams(foodPer100g(item.food), item.grams) : null
          const expanded = editing === item.key
          return (
            <div key={item.key}>
              <ListRow
                last={i === items.length - 1 && !expanded}
                title={item.food ? foodDisplayName(item.food) : item.heard}
                subtitle={
                  item.food
                    ? `${Math.round(item.grams)} ${item.food.unit}`
                    : `${Math.round(item.grams)} g · neatpažinta`
                }
                value={preview ? Math.round(preview.kcal) : '—'}
                meta={preview ? 'kcal' : undefined}
                onActivate={() => setEditing(expanded ? null : item.key)}
              />
              {expanded && (
                <div className="flex flex-col gap-3 border-b border-line bg-raised px-3 py-3">
                  <NumberField
                    label="Kiekis"
                    suffix={item.food?.unit ?? 'g'}
                    value={item.grams}
                    onChange={(v) => voice.patch(item.key, { grams: v ?? 0 })}
                  />
                  <div className="flex flex-col gap-1.5">
                    <span className="text-xs text-mute">
                      Girdėta „{item.heard}“ — pasirinkite tinkamą
                    </span>
                    <div className="flex flex-wrap gap-1.5">
                      {item.candidates.map((candidate) => (
                        <Pill
                          key={candidate.id}
                          active={candidate.id === item.food?.id}
                          onClick={() => voice.patch(item.key, { food: candidate })}
                          className="whitespace-normal text-left"
                        >
                          {foodDisplayName(candidate)}
                        </Pill>
                      ))}
                      {item.candidates.length === 0 && (
                        <span className="text-sm text-mute">Atitikmenų nerasta</span>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => voice.drop(item.key)}
                    className="self-start text-sm text-danger"
                  >
                    Pašalinti eilutę
                  </button>
                </div>
              )}
            </div>
          )
        })}
      </List>
    </>
  )
}

/**
 * The button itself.
 *
 * Pointer events rather than click: a press and a release are two different
 * moments and the recogniser needs both. `setPointerCapture` keeps the release
 * attached to this button even when a thumb slides off it mid-sentence, which
 * on a phone it always does.
 */
function HoldToTalk({
  phase,
  onStart,
  onStop,
}: {
  phase: VoiceLog['phase']['name']
  onStart: () => void
  onStop: () => void
}) {
  const held = useRef(false)

  const begin = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (phase === 'working') return
    event.currentTarget.setPointerCapture(event.pointerId)
    held.current = true
    onStart()
  }
  const end = () => {
    if (!held.current) return
    held.current = false
    onStop()
  }

  const listening = phase === 'listening'
  const working = phase === 'working'
  const workingLabel = useSassOnce(
    working ? 'working' : null,
    () => sassLine('loading', { surface: 'voice' }) ?? 'Atpažįstama…',
  )

  return (
    <button
      type="button"
      onPointerDown={begin}
      onPointerUp={end}
      onPointerCancel={end}
      disabled={working}
      className={`flex select-none flex-col items-center justify-center gap-3 py-6 text-base transition-colors ${
        working ? 'text-mute' : 'text-dim'
      }`}
    >
      <span className="relative flex items-center justify-center">
        {listening && (
          <span
            aria-hidden="true"
            className="absolute size-[104px] rounded-full bg-accent/25"
            style={{ animation: 'voice-pulse 1.2s ease-out infinite' }}
          />
        )}
        <span
          className={`relative grid size-[92px] place-items-center rounded-full border-[3px] transition-colors ${
            listening ? 'border-accent bg-accent text-black' : 'border-line bg-raised text-ink'
          }`}
        >
          {working ? (
            <GlitimeMark size={28} alive station="busy" />
          ) : (
            <MicIcon className="size-9" />
          )}
        </span>
      </span>
      {working
        ? workingLabel
        : listening
          ? 'Klausoma… paleiskite, kai baigsite'
          : 'Laikykite ir kalbėkite'}
    </button>
  )
}
